<div class="file-manager-container">
  <div class="file-manager-layout">
    <!-- Header -->
    <div class="file-manager-header">
      <div class="header-content">
        <div class="header-info">
          <div class="header-icon">
            <i class="fas fa-folder-open"></i>
          </div>
          <div class="header-text">
            <h3 class="file-manager-title">File Manager</h3>
            <p class="file-manager-subtitle">Browse and manage your files</p>
          </div>
        </div>
        <div class="header-actions">
          <button class="action-button primary" (click)="createFolder()" title="Create Folder">
            <i class="fas fa-folder-plus"></i>
            <span class="button-tooltip">New Folder</span>
          </button>
          <label class="action-button primary" title="Upload File" style="cursor: pointer;">
            <i class="fas fa-cloud-upload-alt"></i>
            <input type="file" (change)="onFileUpload($event)" style="display: none;" multiple />
            <span class="button-tooltip">Upload</span>
          </label>
          <div class="action-divider"></div>
          <button class="action-button" (click)="goBack()" [disabled]="breadcrumbs.length <= 1" title="Go Back">
            <i class="fas fa-chevron-left"></i>
            <span class="button-tooltip">Back</span>
          </button>
          <button class="action-button" (click)="refresh()" title="Refresh">
            <i class="fas fa-redo-alt" [class.fa-spin]="isLoading"></i>
            <span class="button-tooltip">Refresh</span>
          </button>
          <button class="action-button" (click)="toggleViewMode()" [title]="viewMode === 'grid' ? 'List View' : 'Grid View'">
            <i class="fas" [ngClass]="viewMode === 'grid' ? 'fa-list-ul' : 'fa-th-large'"></i>
            <span class="button-tooltip">{{ viewMode === 'grid' ? 'List View' : 'Grid View' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="file-manager-toolbar">
      <div class="toolbar-left">
        <div class="breadcrumbs">
          @for (crumb of breadcrumbs; track crumb.path; let last = $last) {
            <span class="breadcrumb-item">
              @if (!last) {
                <a (click)="navigateTo(crumb.path)">{{ crumb.name }}</a>
                <i class="fas fa-chevron-right"></i>
              } @else {
                <span class="current">{{ crumb.name }}</span>
              }
            </span>
          }
        </div>
      </div>
      <div class="toolbar-right">
        <div class="sort-controls">
          <span class="sort-label">Sort:</span>
          <button
            class="sort-button"
            [class.active]="sortBy === 'name'"
            (click)="setSortBy('name')"
            title="Sort by Name">
            <i class="fas fa-sort-alpha-down"></i> <span>Name</span>
          </button>
          <button
            class="sort-button"
            [class.active]="sortBy === 'date'"
            (click)="setSortBy('date')"
            title="Sort by Date">
            <i class="far fa-calendar-alt"></i> <span>Date</span>
          </button>
          <button
            class="sort-button"
            [class.active]="sortBy === 'size'"
            (click)="setSortBy('size')"
            title="Sort by Size">
            <i class="fas fa-weight-hanging"></i> <span>Size</span>
          </button>
          <button
            class="sort-button"
            [class.active]="sortBy === 'type'"
            (click)="setSortBy('type')"
            title="Sort by Type">
            <i class="fas fa-tags"></i> <span>Type</span>
          </button>
          @if (sortOrder === 'asc') {
            <button class="sort-order-button" (click)="setSortBy(sortBy)" title="Descending">
              <i class="fas fa-sort-up"></i>
            </button>
          } @else {
            <button class="sort-order-button" (click)="setSortBy(sortBy)" title="Ascending">
              <i class="fas fa-sort-down"></i>
            </button>
          }
        </div>
        <div class="search-box">
          <i class="fas fa-search search-icon"></i>
          <input
            type="text"
            placeholder="Search files..."
            [(ngModel)]="searchQuery"
            (input)="onSearch($any($event.target).value)"
            class="search-input"
          />
          @if (searchQuery) {
            <button class="clear-search" (click)="searchQuery = ''; onSearch('')" title="Clear search">
              <i class="fas fa-times-circle"></i>
            </button>
          }
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="file-manager-main">
      @if (isLoading) {
        <div class="loading-state">
          <div class="spinner">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
          <p>Loading files...</p>
        </div>
      } @else if (error) {
        <div class="error-state">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ error }}</p>
          <button class="retry-button" (click)="refresh()">
            <i class="fas fa-redo-alt"></i> <span>Retry</span>
          </button>
        </div>
      } @else if (getDisplayItems().length === 0) {
        <div class="empty-state">
          <i class="fas fa-folder-open"></i>
          <p>No files found</p>
          @if (searchQuery) {
            <button class="clear-search-button" (click)="searchQuery = ''; onSearch('')">
              Clear search
            </button>
          }
        </div>
      } @else {
        <!-- Grid View -->
        @if (viewMode === 'grid') {
          <div class="items-grid">
            @for (item of getDisplayItems(); track item.id) {
              <div
                class="item-card"
                [class.folder]="item.type === 'folder'"
                [class.file]="item.type === 'file'"
                [class.selected]="selectedItems.has(item.id)"
                (click)="selectItem(item, $event)"
                (dblclick)="item.type === 'folder' ? navigateToFolder(item) : previewFile(item)">
                <div class="item-thumbnail">
                  @if (item.thumbnail) {
                    <img [src]="item.thumbnail" [alt]="item.name + ' thumbnail'" [title]="item.name || 'File or folder'" loading="lazy" />
                  } @else {
                    <div class="item-icon" [style.color]="getFileIconColor(item)">
                      <i class="fas" [ngClass]="getFileIcon(item)"></i>
                    </div>
                  }
                  @if (item.type === 'folder') {
                    <div class="folder-badge">
                      <i class="fas fa-folder-open"></i>
                    </div>
                  }
                  @if (selectedItems.has(item.id)) {
                    <div class="selection-badge">
                      <i class="fas fa-check-circle"></i>
                    </div>
                  }
                </div>
                <div class="item-info">
                  <div class="item-name" [title]="item.name">{{ item.name }}</div>
                  <div class="item-meta">
                    @if (item.type === 'file') {
                      <span class="item-size">{{ formatFileSize(item.size) }}</span>
                      <span class="item-separator">•</span>
                    }
                    <span class="item-date">{{ formatDate(item.modified) }}</span>
                  </div>
                </div>
              </div>
            }
          </div>
        }

        <!-- List View -->
        @if (viewMode === 'list') {
          <div class="items-list">
            <div class="list-header">
              <div class="list-col-name">Name</div>
              <div class="list-col-type">Type</div>
              <div class="list-col-size">Size</div>
              <div class="list-col-date">Modified</div>
            </div>
            <div class="list-items">
              @for (item of getDisplayItems(); track item.id) {
                <div
                  class="list-item"
                  [class.folder]="item.type === 'folder'"
                  [class.file]="item.type === 'file'"
                  [class.selected]="selectedItems.has(item.id)"
                  (click)="selectItem(item, $event)"
                  (dblclick)="item.type === 'folder' ? navigateToFolder(item) : previewFile(item)">
                  <div class="list-col-name">
                    <i class="fas" [ngClass]="getFileIcon(item)" [style.color]="getFileIconColor(item)"></i>
                    <span [title]="item.name">{{ item.name }}</span>
                  </div>
                  <div class="list-col-type">{{ item.type === 'folder' ? 'Folder' : (item.mimeType || 'File') }}</div>
                  <div class="list-col-size">{{ formatFileSize(item.size) }}</div>
                  <div class="list-col-date">{{ formatDate(item.modified) }}</div>
                </div>
              }
            </div>
          </div>
        }
      }
    </div>

    <!-- Footer/Status Bar -->
    <div class="file-manager-footer">
      <div class="footer-info">
        @if (selectedItems.size > 0) {
          <span class="selection-count">{{ selectedItems.size }} item(s) selected</span>
        } @else {
          <span class="item-count">{{ getDisplayItems().length }} item(s)</span>
        }
      </div>
      <div class="footer-actions">
        @if (selectedItems.size > 0) {
          <button class="footer-button danger" (click)="deleteSelected()">
            <i class="fas fa-trash-alt"></i> <span>Delete Selected</span>
          </button>
          <button class="footer-button" (click)="selectedItems.clear()">
            <i class="fas fa-times-circle"></i> <span>Clear Selection</span>
          </button>
        }
      </div>
    </div>
  </div>

  <!-- Preview Modal -->
  @if (showPreview && previewItem) {
    <div class="preview-overlay" (click)="closePreview()">
      <div class="preview-modal" (click)="$event.stopPropagation()">
        <div class="preview-header">
          <div class="preview-title">
            <i class="fas" [ngClass]="getFileIcon(previewItem)" [style.color]="getFileIconColor(previewItem)"></i>
            <span>{{ previewItem.name }}</span>
          </div>
          <button class="preview-close" (click)="closePreview()" title="Close">
            <i class="fas fa-times-circle"></i>
          </button>
        </div>
        <div class="preview-content">
          @if (previewItem.thumbnail) {
            <img [src]="previewItem.thumbnail" [alt]="previewItem.name + ' preview'" [title]="previewItem.name || 'Preview'" class="preview-image" loading="lazy" />
          } @else {
            <div class="preview-placeholder">
              <i class="fas" [ngClass]="getFileIcon(previewItem)" [style.color]="getFileIconColor(previewItem)"></i>
              <p>Preview not available</p>
            </div>
          }
        </div>
        <div class="preview-footer">
          <div class="preview-info">
            <div class="info-row">
              <span class="info-label">Size:</span>
              <span class="info-value">{{ formatFileSize(previewItem.size) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Modified:</span>
              <span class="info-value">{{ formatDate(previewItem.modified) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Type:</span>
              <span class="info-value">{{ previewItem.mimeType || 'Unknown' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  }
</div>
